/**
 * sessionStorage persistence so a conversation survives villamine.com's
 * full-page navigations (WebForms postbacks and links). Nothing personal is
 * stored beyond what the visitor typed into the widget in this tab; the data
 * is discarded when the tab closes. All access is guarded: private mode or
 * blocked storage simply means no persistence.
 */
import type { ConversationState, SearchResponse } from "../domain/api";

/** Bump when SearchResponse/ConversationState shapes change; older payloads are discarded. */
export const PERSISTED_SCHEMA = 2;

export interface PersistedWidget {
  v: typeof PERSISTED_SCHEMA;
  saved_at: number;
  open: boolean;
  state: ConversationState;
  response: SearchResponse | null;
}

const MAX_AGE_MS = 6 * 60 * 60_000;

function storage(win: Window | null | undefined): Storage | null {
  try {
    return win?.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export function loadPersisted(win: Window | null | undefined, key: string): PersistedWidget | null {
  const s = storage(win);
  if (!s) return null;
  try {
    const raw = s.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedWidget;
    if (parsed?.v !== PERSISTED_SCHEMA || typeof parsed.saved_at !== "number" || Date.now() - parsed.saved_at > MAX_AGE_MS) return null;
    if (!parsed.state || typeof parsed.state !== "object" || !Array.isArray(parsed.state.displayed)) return null;
    if (parsed.response !== null) {
      const r = parsed.response;
      if (!r || typeof r !== "object" || !Array.isArray(r.cards) || !Array.isArray(r.partial_cards) || !Array.isArray(r.notices) || !Array.isArray(r.relaxations)) return null;
      if ([...r.cards, ...r.partial_cards].some((c) => !c || !Array.isArray(c.matched) || !Array.isArray(c.matched_derived) || !Array.isArray(c.unconfirmed))) return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function savePersisted(win: Window | null | undefined, key: string, value: Omit<PersistedWidget, "v" | "saved_at">): void {
  const s = storage(win);
  if (!s) return;
  try {
    s.setItem(key, JSON.stringify({ v: PERSISTED_SCHEMA, saved_at: Date.now(), ...value } satisfies PersistedWidget));
  } catch {
    /* quota or blocked storage: ignore */
  }
}

export function clearPersisted(win: Window | null | undefined, key: string): void {
  try {
    storage(win)?.removeItem(key);
  } catch {
    /* ignore */
  }
}
